// Dropdown for field editorial status
// Allows setting status: in_progress, ai_review, marked_for_review, validated
import { useState, useRef, useEffect, useCallback } from 'react';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { useFieldMetadata } from '../context/FieldMetadataContext';
import { useFormMode } from '../context/FormModeContext';
import type { FieldStatus } from '../types/fields';

interface FieldStatusDropdownProps {
  fieldName: string;
  // Controls outer wrapper width so parent can size the dropdown as needed
  className?: string;
}

// Hex colors aligned with FIELD_STATUS_CONFIG in StartupProfileForm.
// Selectable statuses only — the user picks one of these from the menu.
const STATUS_CONFIG: { value: FieldStatus; color: string }[] = [
  { value: 'in_progress', color: '#FFDD53' },
  { value: 'ai_review', color: '#42BEFC' },
  { value: 'marked_for_review', color: '#EF7700' },
  { value: 'validated', color: '#20BD5A' },
];

// 'incomplete' ("To start") is display-only: shown as the current status when
// a field hasn't been touched yet, but never offered as a menu choice —
// the user can't manually set a field back to "not started".
const INCOMPLETE_STATUS = { value: 'incomplete' as FieldStatus, color: '#A8A8A8' };

export function FieldStatusDropdown({ fieldName, className = '' }: FieldStatusDropdownProps) {
  const { t } = useTranslation('common');
  const { getMetadata, setStatus } = useFieldMetadata();
  const { isReadOnly } = useFormMode();
  const metadata = getMetadata(fieldName);

  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<CSSProperties>({});
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);

  // Section accordions clip absolutely-positioned children with overflow:hidden
  // (needed for their expand/collapse animation), so this menu uses fixed
  // positioning computed from the trigger's rect — same pattern as
  // CountryMultiselectField — instead of relying on the DaisyUI CSS-only
  // .dropdown/.dropdown-content, which was getting clipped/hidden.
  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setMenuStyle({ top: rect.bottom + 4, left: rect.left, minWidth: Math.max(rect.width, 200) });
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    updatePosition();
    window.addEventListener('scroll', updatePosition, { capture: true, passive: true });
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, { capture: true });
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, updatePosition]);

  useEffect(() => {
    if (!isOpen) return;
    const handleOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!triggerRef.current?.contains(target) && !menuRef.current?.contains(target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    return () => document.removeEventListener('mousedown', handleOutside);
  }, [isOpen]);

  // Merge static config with translated labels at render time
  const statusOptions = STATUS_CONFIG.map((opt) => ({
    ...opt,
    label: t(`fieldStatus.${opt.value}`),
  }));

  // The trigger can display 'incomplete' even though it's not a menu option.
  const currentOption =
    [
      ...statusOptions,
      { ...INCOMPLETE_STATUS, label: t(`fieldStatus.${INCOMPLETE_STATUS.value}`) },
    ].find((opt) => opt.value === metadata.status) || statusOptions[0];

  const pillClass =
    'w-full flex items-center justify-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-colors';
  const pillStyle = { backgroundColor: `${currentOption.color}26`, color: currentOption.color };
  const pillContent = (
    <>
      <span
        className="w-2 h-2 rounded-full shrink-0"
        style={{ backgroundColor: currentOption.color }}
      />
      <span className="truncate">{currentOption.label}</span>
    </>
  );

  // Read-only: a plain badge, not a control.
  if (isReadOnly) {
    return (
      <div className={`min-w-0 ${className}`}>
        <span className={`${pillClass} cursor-default`} style={pillStyle}>
          {pillContent}
        </span>
      </div>
    );
  }

  return (
    // Escape closes the menu and returns focus to the trigger (disclosure pattern).
    <div
      className={`min-w-0 ${className}`}
      onKeyDown={(e) => {
        if (e.key !== 'Escape' || !isOpen) return;
        e.stopPropagation();
        setIsOpen(false);
        triggerRef.current?.focus();
      }}
    >
      {/* Trigger: rounded pill badge, translucent tinted bg matching the status color.
          A real <button> (was a focusable <label>): Enter/Space work and AT announces it. */}
      <button
        type="button"
        ref={triggerRef}
        aria-expanded={isOpen}
        onClick={() => setIsOpen((prev) => !prev)}
        className={`${pillClass} cursor-pointer`}
        style={pillStyle}
      >
        {pillContent}
        <svg className="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown menu — only rendered when open */}
      {isOpen && (
        <ul
          ref={menuRef}
          style={menuStyle}
          className="fixed z-[999] py-1 bg-[#2a2a2a] opacity-100 rounded-lg shadow-lg border border-white/10"
        >
          {statusOptions.map((option) => (
            <li key={option.value}>
              <button
                type="button"
                onClick={() => {
                  setStatus(fieldName, option.value);
                  setIsOpen(false);
                }}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left hover:bg-white/10 transition-colors ${
                  metadata.status === option.value ? 'bg-white/10 text-white' : 'text-gray-300'
                }`}
              >
                <span
                  className="w-2 h-2 rounded-full shrink-0"
                  style={{ backgroundColor: option.color }}
                />
                {option.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
